import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import { merge, Observable, Subject } from 'rxjs';
import { first, map, startWith, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

abstract class BudgetLine {
  public readonly type: 'AMOUNT' | 'RATE';
  public label?: string;
}

class PriceAmountLine extends BudgetLine {
  public readonly type = 'AMOUNT' as const;
  public quantity: number;
  public unit?: string;
  public unitPrice: number;
}

class AdjustmentRateLine extends BudgetLine {
  public readonly type = 'RATE' as const;
  public rate: number;
}

interface PriceAmountFormGroup {
  id: FormControl<string>;
  label: FormControl<string>;
  quantity: FormControl<number>;
  unit: FormControl<string>;
  unitPrice: FormControl<number>;
  subtotal: FormControl<number>;
  type: FormControl<'AMOUNT'>;
}

interface AdjustmentRateFormGroup {
  id: FormControl<string>;
  label: FormControl<string>;
  rate: FormControl<number>;
  subtotal: FormControl<number>;
  type: FormControl<'RATE'>;
}

@Component({
  selector: 'budget-field-builder',
  templateUrl: './budget-field-builder.component.html',
  styleUrls: ['./budget-field-builder.component.scss'],
  standalone: false
})
export class BudgetFieldBuilderComponent extends AbstractFieldBuilder<FormArray<FormGroup<PriceAmountFormGroup> | FormGroup<AdjustmentRateFormGroup>>> implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);

  declare public form: FormGroup<{
    field: FormArray<FormGroup<PriceAmountFormGroup> | FormGroup<AdjustmentRateFormGroup>>
  }>;
  public budgetTotal: number;
  public filteredUnits: Observable<string[]>;
  public unitChanges = new Subject<string>();

  /**
   * Initialize field value, state and create initial budget rows.
   */
  public ngOnInit(): void {
    this.form = this.fb.group({
      field: this.fb.array<FormGroup>([])
    }, {validators: this.computeValidators()});
    this.setFieldValue(this.fieldInitValue || []);
    this.setFieldInitialValue(this.fieldInitValue || []);
    this.getNextState();
    this.redrawBudgetRows();

    // Fetch units to show in autocomplete panel
    const units = this.fieldConfig.field.fieldValues as string[];
    this.filteredUnits = this.unitChanges.pipe(
      startWith(''),
      map((value: string) => units.filter(unit => unit.includes(value)))
    );
  }

  /**
   * Setup hooks.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();
  }

  /**
   * Get the current list of budget rows representing the field's value.
   * @return Field value.
   */
  public getFieldValue(): BudgetLine[] {
    return super.getFieldValue() as BudgetLine[];
  }

  /**
   * Save value and reset budget rows to match the new value.
   */
  public onClickSave(): void {
    super.onClickSave();
    this.redrawBudgetRows();
  }

  /**
   * Cancel changes and reset budget rows to initial value.
   */
  public onClickCancel(): void {
    super.onClickCancel();
    this.redrawBudgetRows();
  }

  /**
   * Calculate the total value of the budget. Multiply each rows quantity by its unit price then add all results.
   */
  private calculateBudgetTotal(): void {
    this.budgetTotal = this.getFieldValue()?.reduce((total: number, entry: BudgetLine, index: number) => {
      let subtotal: number;

      if (this.isAdjustmentRateLine(entry)) {
        subtotal = entry.rate / 100 * total;
      } else if (this.isPriceAmountLine(entry)) {
        subtotal = entry.quantity * entry.unitPrice;
      }
      this.form.controls.field.at(index)?.controls.subtotal.patchValue(subtotal);
      return total + subtotal;
    }, 0);
  }

  /**
   * Add a new price amount row to the form. Optional data can be passed to fill the new fields, otherwise empty initial values are
   * used, except for quantity that is set to 1 by default.
   * @param data Optional data to fill the new row's fields.
   */
  public addPriceAmountRow(data?: PriceAmountLine): void {
    const group = this.fb.group<PriceAmountFormGroup>({
      id: this.fb.control({value: crypto.randomUUID(), disabled: true}),
      label: this.fb.control(data?.label || '', Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)),
      quantity: this.fb.control(data?.quantity || 1, Validators.compose([
        Validators.required,
        Validators.min(0)
      ])),
      unit: this.fb.control(data?.unit || '', Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)),
      unitPrice: this.fb.control(data?.unitPrice, Validators.compose([
        Validators.required,
        Validators.min(0)
      ])),
      subtotal: this.fb.control({value: 0, disabled: true}),
      type: this.fb.control('AMOUNT')
    });

    // Only subscribe to changes in input fields
    merge(
      group.controls.label.valueChanges,
      group.controls.quantity.valueChanges,
      group.controls.unit.valueChanges,
      group.controls.unitPrice.valueChanges
    ).pipe(
      // Each time these fields update, the form might not have updated yet
      switchMap(() => this.form.valueChanges.pipe(first())),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe(() => {
        this.updateFormStateService();
      });

    // Merge all valueChanges events from unit fields
    group.controls.unit.valueChanges.subscribe((value => this.unitChanges.next(value)));

    this.form.controls.field.push(group, {emitEvent: false});
  }

  /**
   * Add a new adjustment rate row to the form. Optional data can be passed to fill the new fields.
   * @param data Optional data to fill the new row's fields.
   */
  public addAdjustmentRateRow(data?: AdjustmentRateLine): void {
    const group = this.fb.group<AdjustmentRateFormGroup>({
      id: this.fb.control({value: crypto.randomUUID(), disabled: true}),
      label: this.fb.control(data?.label || '', Validators.maxLength(this.appConfig.FIELD_MAX_LENGTH)),
      rate: this.fb.control(data?.rate || 0, Validators.compose([
        Validators.required,
        Validators.min(0)
      ])),
      subtotal: this.fb.control({value: 0, disabled: true}),
      type: this.fb.control('RATE')
    });

    // Only subscribe to changes in input fields
    merge(
      group.controls.label.valueChanges,
      group.controls.rate.valueChanges
    )
      .pipe(
        // Each time this field updates, the form might not have updated yet
        switchMap(() => this.form.valueChanges.pipe(first())),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.updateFormStateService();
      });

    this.form.controls.field.push(group, {emitEvent: false});
  }

  /**
   * Remove a budget row at the given index.
   * @param index Index of row to remove.
   */
  public removeBudgetRow(index: number): void {
    this.form.controls.field.removeAt(index);
    this.updateFormStateService();
  }

  /**
   * Update the entire budget widget's value in the form state service
   * when a field changes in one of the rows, or an entire row is removed.
   * @private
   */
  private updateFormStateService(): void {
    // The disabled fields are excluded from the form value
    this.setFieldValue(this.form.controls.field.value);
    this.calculateBudgetTotal();
  }

  /**
   * Clear all rows and create new rows based on the field's value. To reset to original value, first reset the field's
   * value to its original value.
   * @private
   */
  private redrawBudgetRows(): void {
    this.form.controls.field.clear();
    this.getFieldValue()?.forEach(budgetEntry => {
      if (this.isAdjustmentRateLine(budgetEntry)) {
        this.addAdjustmentRateRow(budgetEntry);
      } else if (this.isPriceAmountLine(budgetEntry)) {
        this.addPriceAmountRow(budgetEntry);
      }
    });
    this.calculateBudgetTotal();
  }

  // TODO TTT-4135 Remove, iterate over errors instead of validators
  /**
   * List of validators for which an error has been raised by the corresponding validator function.
   */
  public override get erroredValidators(): FieldValidator[] {
    return this.fieldConfig.field.validators.filter(validator => !!this.form.errors?.[validator.code]);
  }

  /**
   * Type guard for PriceAmountLine
   */
  protected isPriceAmountLine(line: BudgetLine): line is PriceAmountLine {
    return line.type === 'AMOUNT';
  }

  /**
   * Type guard for PriceAmountFormGroup
   */
  protected isPriceAmountForm(form: FormGroup<PriceAmountFormGroup> | FormGroup<AdjustmentRateFormGroup>): form is FormGroup<PriceAmountFormGroup> {
    return form.value.type === 'AMOUNT';
  }

  /**
   * Type guard for AdjustmentRateLine
   */
  protected isAdjustmentRateLine(line: BudgetLine): line is AdjustmentRateLine {
    return line.type === 'RATE';
  }
}
